import { DownloadIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export type ExportFormat = 'csv' | 'json';

interface ExportButtonProps {
  onExport: (format: ExportFormat) => void;
  isExporting: boolean;
  disabled?: boolean;
  /**
   * `default` — primary text button (global export, no competing CTA).
   * `outline` — neutral-outline text button (sits beside a primary CTA, e.g. "Add").
   * `icon` — neutral-outline icon-only button (per-widget export).
   * `ghost-icon` — borderless icon-only button (sits in a card action row with other ghost icons).
   */
  variant?: 'default' | 'outline' | 'icon' | 'ghost-icon';
}

export const ExportButton = ({
  onExport,
  isExporting,
  disabled,
  variant = 'default',
}: ExportButtonProps) => {
  const { t } = useTranslation();
  const isDisabled = isExporting || disabled;

  const renderTrigger = () => {
    if (variant === 'icon' || variant === 'ghost-icon') {
      return (
        <InfoTooltip content={t('common.export')} showArrow>
          <DropdownMenuTrigger asChild>
            <Button
              variant={variant === 'ghost-icon' ? 'ghost-neutral' : 'outline-neutral'}
              size="icon-sm"
              disabled={isDisabled}
              isLoading={isExporting}
              aria-label={t('common.export')}
            >
              <DownloadIcon size={16} />
            </Button>
          </DropdownMenuTrigger>
        </InfoTooltip>
      );
    }

    return (
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant === 'outline' ? 'outline-neutral' : 'primary'}
          size="sm"
          isLoading={isExporting}
          disabled={isDisabled}
        >
          <DownloadIcon size={16} />
          {t('common.export')}
        </Button>
      </DropdownMenuTrigger>
    );
  };

  return (
    <DropdownMenu>
      {renderTrigger()}
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => onExport('csv')}>
          {t('common.export')} CSV
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onExport('json')}>
          {t('common.export')} JSON
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
